/**
 * 2D overlay for rendering diameter measurements.
 *
 * Renders a dashed line between two diameter endpoints (p1, p2) with a legend
 * on an extension line past p2, perpendicular tick marks at both endpoints,
 * and interactive hit zones when editable.
 */

import { distance } from '../../domain';
import type { Vec3 } from '../../domain';
import type { IViewport } from '../../viewports';
import type { IOverlayObject } from '../types';

export interface DiameterOverlayConfig {
    readonly id: string;
    readonly p1: Vec3;
    readonly p2: Vec3;
    readonly value: number;
    readonly label: string;
    readonly color?: string;
    readonly edited?: boolean;
}

export class DiameterOverlay implements IOverlayObject {
    readonly id: string;
    readonly zIndex = 150;
    interactive = false;
    readonly clipByCamera = false;
    visible = true;

    private p1World: Vec3;
    private p2World: Vec3;
    private p1Canvas = { x: 0, y: 0 };
    private p2Canvas = { x: 0, y: 0 };
    private value: number;
    private label: string;
    private color: string;
    private edited: boolean;

    // Visual constants (matching POC MeasurementElement)
    private readonly lineWidth = 3;
    private readonly tickLength = 20;
    private readonly arrowLength = 20;
    private readonly handleHitRadius = 20;
    private readonly font = "14px 'JetBrains Mono', monospace";
    private readonly dashPattern: number[] = [10, 5];
    private readonly resetButtonRadius = 9;

    // Interaction state (set by DiameterEditTool)
    hoveredHandle: 'p1' | 'p2' | null = null;
    draggingHandle: 'p1' | 'p2' | null = null;
    hoveredResetButton = false;

    /** Canvas-space bounding box of the reset button (set during render). */
    private resetButtonBounds: { x: number; y: number; w: number; h: number } | null = null;

    constructor(config: DiameterOverlayConfig) {
        this.id = config.id;
        this.p1World = config.p1;
        this.p2World = config.p2;
        this.value = config.value;
        this.label = config.label;
        this.color = config.color ?? 'rgb(102, 204, 255)';
        this.edited = config.edited ?? false;
    }

    updateDiameter(p1: Vec3, p2: Vec3, value: number, edited: boolean): void {
        this.p1World = p1;
        this.p2World = p2;
        this.value = value;
        this.edited = edited;
    }

    // ── Position accessors (used by DiameterEditTool) ──

    getP1World(): Vec3 {
        return [...this.p1World] as Vec3;
    }

    getP2World(): Vec3 {
        return [...this.p2World] as Vec3;
    }

    getP1Canvas(): { x: number; y: number } {
        return { ...this.p1Canvas };
    }

    getP2Canvas(): { x: number; y: number } {
        return { ...this.p2Canvas };
    }

    setP1World(pos: Vec3): void {
        this.p1World = [...pos] as Vec3;
    }

    setP2World(pos: Vec3): void {
        this.p2World = [...pos] as Vec3;
    }

    getHandleHitRadius(): number {
        return this.handleHitRadius;
    }

    hitTest(canvasX: number, canvasY: number): boolean {
        if (!this.interactive) return false;

        if (this.hitTestResetButton(canvasX, canvasY)) return true;

        for (const p of [this.p1Canvas, this.p2Canvas]) {
            const dx = canvasX - p.x;
            const dy = canvasY - p.y;
            if (dx * dx + dy * dy <= this.handleHitRadius * this.handleHitRadius) {
                return true;
            }
        }
        return false;
    }

    hitTestResetButton(canvasX: number, canvasY: number): boolean {
        if (!this.interactive || !this.edited || !this.resetButtonBounds) return false;
        const b = this.resetButtonBounds;
        return canvasX >= b.x && canvasX <= b.x + b.w && canvasY >= b.y && canvasY <= b.y + b.h;
    }

    onCameraChanged(_viewport: IViewport): void {
        // Canvas positions are updated in render()
    }

    // ═══════════════════════════════════════════
    //  Rendering
    // ═══════════════════════════════════════════

    render(ctx: CanvasRenderingContext2D, viewport: IViewport): void {
        const proj1 = viewport.worldToCanvas(this.p1World);
        const proj2 = viewport.worldToCanvas(this.p2World);
        if (!proj1.visible && !proj2.visible) return;

        this.p1Canvas = { x: proj1.x, y: proj1.y };
        this.p2Canvas = { x: proj2.x, y: proj2.y };

        const { p1Canvas: p1, p2Canvas: p2 } = this;
        const dx = p2.x - p1.x;
        const dy = p2.y - p1.y;
        const lineLen = Math.sqrt(dx * dx + dy * dy);
        if (lineLen < 1) return;

        const lineColor = this.edited || this.draggingHandle ? '#ffaa00' : this.color;

        ctx.save();

        // Shadow effect (matching POC)
        ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
        ctx.shadowBlur = 1;

        ctx.strokeStyle = lineColor;
        ctx.fillStyle = lineColor;
        ctx.lineWidth = this.lineWidth;

        // ── Dashed line between p1 and p2 ──
        ctx.setLineDash(this.dashPattern);
        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
        ctx.stroke();
        ctx.setLineDash([]);

        // ── Extension line from p1 in opposite direction ──
        const dirX = dx / lineLen;
        const dirY = dy / lineLen;
        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p1.x - dirX * this.arrowLength, p1.y - dirY * this.arrowLength);
        ctx.stroke();

        // ── Legend on extension line past p2 ──
        this.renderLegend(ctx, p1, p2, dirX, dirY, lineColor);

        // ── Perpendicular ticks with black outline ──
        this.renderTicks(ctx, p1, p2, lineLen, lineColor);

        // ── Hit zones (when interactive) ──
        if (this.interactive) {
            this.renderHitZone(ctx, p1.x, p1.y, this.hoveredHandle === 'p1', this.draggingHandle === 'p1');
            this.renderHitZone(ctx, p2.x, p2.y, this.hoveredHandle === 'p2', this.draggingHandle === 'p2');
        }

        ctx.restore();
    }

    // ═══════════════════════════════════════════
    //  Private rendering helpers
    // ═══════════════════════════════════════════

    private renderLegend(
        ctx: CanvasRenderingContext2D,
        p1: { x: number; y: number },
        p2: { x: number; y: number },
        dirX: number,
        dirY: number,
        color: string
    ): void {
        const displayValue = this.draggingHandle ? distance(this.p1World, this.p2World) : this.value;
        const legend = `${this.label}: ${displayValue.toFixed(1)} mm`;

        ctx.font = this.font;
        const metrics = ctx.measureText(legend);
        const textWidth = metrics.width;
        const textMargin = this.arrowLength;
        const totalTextWidth = textWidth + textMargin;

        // Extension point past p2
        const p2ExtX = p2.x + dirX * (this.arrowLength + 2);
        const p2ExtY = p2.y + dirY * (this.arrowLength + 2);

        // Draw extension line from p2 to p2Ext
        ctx.strokeStyle = color;
        ctx.beginPath();
        ctx.moveTo(p2.x, p2.y);
        ctx.lineTo(p2ExtX, p2ExtY);
        ctx.stroke();

        // Determine if text should be on left or right
        const textOnLeft = p2.x < p1.x;

        // Draw horizontal underline for the legend
        const endTextX = p2ExtX + (textOnLeft ? -1 : 1) * totalTextWidth;

        ctx.beginPath();
        ctx.moveTo(p2ExtX, p2ExtY);
        ctx.lineTo(endTextX, p2ExtY);
        ctx.stroke();

        // Calculate text anchor position
        const textAnchorOffsetX = (textOnLeft ? -1 : 0) * totalTextWidth + (textOnLeft ? 0 : 1) * textMargin;
        const textAnchorX = p2ExtX + textAnchorOffsetX;
        const textAnchorY = p2ExtY - 2;

        // Draw text
        ctx.fillStyle = color;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'bottom';
        ctx.fillText(legend, textAnchorX, textAnchorY);

        // Draw reset button when edited and not dragging
        this.resetButtonBounds = null;
        if (this.edited && !this.draggingHandle) {
            this.renderResetButton(ctx, textAnchorX, textAnchorY, textWidth, textOnLeft, color);
        }
    }

    private renderResetButton(
        ctx: CanvasRenderingContext2D,
        textAnchorX: number,
        textAnchorY: number,
        textWidth: number,
        textOnLeft: boolean,
        _color: string
    ): void {
        const radius = this.resetButtonRadius;
        const gap = 6;

        // Center the button vertically with the text, offset horizontally past the legend
        const buttonX = textOnLeft ? textAnchorX - gap - radius : textAnchorX + textWidth + gap + radius;
        const buttonY = textAnchorY - 7;

        // Store hit-test bounds as a square around the circle
        this.resetButtonBounds = {
            x: buttonX - radius,
            y: buttonY - radius,
            w: radius * 2,
            h: radius * 2,
        };

        const isHovered = this.hoveredResetButton;

        ctx.save();
        ctx.beginPath();
        ctx.arc(buttonX, buttonY, radius, 0, 2 * Math.PI);

        ctx.fillStyle = isHovered ? 'rgba(255, 100, 100, 0.6)' : 'rgba(255, 255, 255, 0.6)';
        ctx.fill();

        ctx.strokeStyle = isHovered ? 'rgba(200, 0, 0, 0.8)' : 'rgb(100, 100, 100)';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        // Draw icon
        ctx.fillStyle = isHovered ? 'rgb(255,255,255)' : 'rgb(60, 60, 60)';
        ctx.font = `${radius * 1.4}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('\u21BA', buttonX, buttonY + 1);

        ctx.restore();
    }

    private renderTicks(
        ctx: CanvasRenderingContext2D,
        p1: { x: number; y: number },
        p2: { x: number; y: number },
        lineLen: number,
        color: string
    ): void {
        const dx = p2.x - p1.x;
        const dy = p2.y - p1.y;
        const perpX = -dy / lineLen;
        const perpY = dx / lineLen;
        const halfTick = this.tickLength / 2;

        // Black outline ticks (thicker, behind)
        ctx.save();
        ctx.lineWidth = 2 * this.lineWidth;
        ctx.strokeStyle = 'rgb(0, 0, 0)';
        this.drawTickPair(ctx, p1, p2, perpX, perpY, halfTick + 2);
        ctx.restore();

        // Colored ticks on top
        ctx.strokeStyle = color;
        ctx.lineWidth = this.lineWidth;
        this.drawTickPair(ctx, p1, p2, perpX, perpY, halfTick);
    }

    private drawTickPair(
        ctx: CanvasRenderingContext2D,
        p1: { x: number; y: number },
        p2: { x: number; y: number },
        perpX: number,
        perpY: number,
        halfLen: number
    ): void {
        // Tick at p1
        ctx.beginPath();
        ctx.moveTo(p1.x + perpX * halfLen, p1.y + perpY * halfLen);
        ctx.lineTo(p1.x - perpX * halfLen, p1.y - perpY * halfLen);
        ctx.stroke();

        // Tick at p2
        ctx.beginPath();
        ctx.moveTo(p2.x + perpX * halfLen, p2.y + perpY * halfLen);
        ctx.lineTo(p2.x - perpX * halfLen, p2.y - perpY * halfLen);
        ctx.stroke();
    }

    private renderHitZone(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        isHovered: boolean,
        isDragging: boolean
    ): void {
        if (!isHovered && !isDragging) return;

        ctx.save();
        ctx.shadowBlur = 0;
        ctx.beginPath();
        ctx.arc(x, y, this.handleHitRadius, 0, Math.PI * 2);
        ctx.strokeStyle = isDragging ? 'rgba(255, 215, 0, 1)' : 'rgba(255, 255, 255, 0.7)';
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.restore();
    }
}
